/**
 * Best-effort reverse-render: LiveWorkflow → `.canary.md` markdown.
 *
 * Used to materialize an existing workflow into the agent workspace so
 * release-QA agents can read coverage with their filesystem tools instead
 * of a structured RPC. Output is **not round-trippable** — humanized labels,
 * no `<!-- @id -->` stamps, no condition-branch reconstruction. Sufficient
 * for gap analysis ("does the suite already cover destructive delete?").
 *
 * @module spec/materialize
 */

import { topoOrderNodes } from '@chatsdet/workflow-diff';
import type { LiveWorkflow } from './diff.js';
import { renderStepStructured, type SpecDocBlock } from './materialize-blocks.js';
import { arrField, strField } from './materialize-fields.js';

export type { SpecAssertion, SpecDocBlock } from './materialize-blocks.js';

export interface MaterializeOptions {
  /** Optional tags to emit in the frontmatter. */
  tags?: string[];
  /** Override the workflow slug used in the file name (caller's responsibility). */
  // (no slug logic in here — caller picks the file name; this just renders content)
}

/**
 * Structured form of the materialized spec. Unlike the flat string from
 * {@link materializeWorkflowToSpec}, every Journey step is its own block keyed
 * by its source `nodeId` + `nodeType`. This preserves node identity so a
 * consumer (e.g. a flow-designer view) can render block-by-block and later
 * overlay per-step run status or swap a block for an inline editor — without
 * re-parsing markdown.
 *
 * Both this and {@link materializeWorkflowToSpec} render nodes through the same
 * private `renderNode`, so the per-node markdown can never drift between them.
 */
export function materializeWorkflowToSpec(
  workflow: LiveWorkflow,
  opts: MaterializeOptions = {}
): string {
  const lines: string[] = [];

  // Header — flags the output as a best-effort read-only view, not the
  // inverse of `emitSpec`. parseSpec ignores HTML comments at the top.
  lines.push('<!--');
  lines.push('  Materialized from a live workflow for agent reading.');
  lines.push('  Best-effort, NOT round-trippable. Do not parse + re-emit.');
  lines.push('-->');
  lines.push('');

  // ── Frontmatter ────────────────────────────────────────────────────────
  const actor = inferActor(workflow);
  lines.push('---');
  lines.push(`name: ${yamlString(workflow.name)}`);
  lines.push(`workflowId: ${workflow.id}`);
  if (workflow.status) lines.push(`status: ${workflow.status}`);
  if (workflow.flowType) lines.push(`flowType: ${workflow.flowType}`);
  if (actor) lines.push(`actor: ${actor}`);
  lines.push(`authenticated: ${actor ? 'true' : 'false'}`);
  if (opts.tags && opts.tags.length > 0) {
    lines.push(`tags: [${opts.tags.map((t) => yamlString(t)).join(', ')}]`);
  }
  lines.push('---');
  lines.push('');

  lines.push(`# ${workflow.name}`);
  lines.push('');

  // ── Spirit ─────────────────────────────────────────────────────────────
  if (workflow.description) {
    lines.push('## Spirit');
    lines.push(`> ${workflow.description.replace(/\n/g, '\n> ')}`);
    lines.push('');
  }

  // ── Preflight / Cleanup (prose) ───────────────────────────────────────
  if (workflow.preflight) {
    lines.push('## Preflight');
    lines.push(workflow.preflight);
    lines.push('');
  }
  if (workflow.cleanup) {
    lines.push('## Cleanup');
    lines.push(workflow.cleanup);
    lines.push('');
  }

  // ── Inputs ─────────────────────────────────────────────────────────────
  const inputs = parseInputs(workflow.inputsJson);
  if (inputs.length > 0) {
    lines.push('## Inputs');
    for (const i of inputs) {
      lines.push(renderInputLine(i));
    }
    lines.push('');
  }

  // ── Scenarios ──────────────────────────────────────────────────────────
  const scenarios = parseScenarios(workflow.scenariosJson);
  if (scenarios.length > 0) {
    lines.push('## Scenarios');
    for (const s of scenarios) {
      const heading = s.isDefault ? `### ${s.name} *(default)*` : `### ${s.name}`;
      lines.push(heading);
      if (s.description) lines.push(`- description: ${s.description}`);
      if (s.values && Object.keys(s.values).length > 0) {
        lines.push('- values:');
        for (const [k, v] of Object.entries(s.values)) {
          lines.push(`    ${k}: ${v}`);
        }
      }
      lines.push('');
    }
  }

  // ── Journey ────────────────────────────────────────────────────────────
  lines.push('## Journey');
  lines.push('');
  const sortedNodes = topoOrderNodes(workflow.nodes, workflow.edges ?? []);
  for (const node of sortedNodes) {
    // Skip the auto-emitted login node (already represented by `actor` frontmatter).
    if (node.nodeType === 'login' && node === sortedNodes[0]) continue;
    lines.push(renderNode(node));
    lines.push('');
  }

  // Trim trailing blank lines, then ensure single newline at EOF.
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  lines.push('');
  return lines.join('\n');
}

/**
 * Structured sibling of {@link materializeWorkflowToSpec}: the same content as
 * a list of identity-keyed blocks. Frontmatter fields, section bodies, and one
 * `journey-step` block per node (carrying `nodeId`/`nodeType`). The auto-emitted
 * leading login node is skipped (it's represented by `actor` in the frontmatter
 * block), mirroring the string renderer.
 */
export function materializeWorkflowToBlocks(
  workflow: LiveWorkflow,
  opts: MaterializeOptions = {}
): SpecDocBlock[] {
  const blocks: SpecDocBlock[] = [];

  // ── Frontmatter ────────────────────────────────────────────────────────
  const actor = inferActor(workflow);
  const fields: Record<string, string> = {
    name: workflow.name,
    workflowId: workflow.id,
  };
  if (workflow.status) fields.status = workflow.status;
  if (workflow.flowType) fields.flowType = workflow.flowType;
  if (actor) fields.actor = actor;
  fields.authenticated = actor ? 'true' : 'false';
  if (opts.tags && opts.tags.length > 0) fields.tags = opts.tags.join(', ');
  blocks.push({ kind: 'frontmatter', fields });

  // ── Spirit / Preflight / Cleanup ───────────────────────────────────────
  if (workflow.description) {
    blocks.push({
      kind: 'section',
      title: 'Spirit',
      markdown: `## Spirit\n> ${workflow.description.replace(/\n/g, '\n> ')}`,
    });
  }
  if (workflow.preflight) {
    blocks.push({
      kind: 'section',
      title: 'Preflight',
      markdown: `## Preflight\n${workflow.preflight}`,
    });
  }
  if (workflow.cleanup) {
    blocks.push({ kind: 'section', title: 'Cleanup', markdown: `## Cleanup\n${workflow.cleanup}` });
  }

  // ── Inputs ─────────────────────────────────────────────────────────────
  const inputs = parseInputs(workflow.inputsJson);
  if (inputs.length > 0) {
    const body = inputs.map(renderInputLine).join('\n');
    blocks.push({ kind: 'section', title: 'Inputs', markdown: `## Inputs\n${body}` });
  }

  // ── Scenarios ──────────────────────────────────────────────────────────
  const scenarios = parseScenarios(workflow.scenariosJson);
  if (scenarios.length > 0) {
    const chunks = scenarios.map((s) => {
      const out = [s.isDefault ? `### ${s.name} *(default)*` : `### ${s.name}`];
      if (s.description) out.push(`- description: ${s.description}`);
      if (s.values && Object.keys(s.values).length > 0) {
        out.push('- values:');
        for (const [k, v] of Object.entries(s.values)) out.push(`    ${k}: ${v}`);
      }
      return out.join('\n');
    });
    blocks.push({
      kind: 'section',
      title: 'Scenarios',
      markdown: `## Scenarios\n\n${chunks.join('\n\n')}`,
    });
  }

  // ── Journey ────────────────────────────────────────────────────────────
  blocks.push({ kind: 'section', title: 'Journey', markdown: '## Journey' });
  const sortedNodes = topoOrderNodes(workflow.nodes, workflow.edges ?? []);
  for (const node of sortedNodes) {
    if (node.nodeType === 'login' && node === sortedNodes[0]) continue;
    const step = renderStepStructured(node);
    blocks.push({
      kind: 'journey-step',
      nodeId: node.id,
      nodeType: node.nodeType,
      ...step,
    });
  }

  return blocks;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function renderNode(node: {
  nodeType: string;
  configJson: Record<string, unknown> | null;
}): string {
  const cfg = node.configJson ?? {};
  switch (node.nodeType) {
    case 'navigate': {
      const url = strField(cfg, 'pageUrl') ?? '?';
      return `**Navigate to** \`${url}\`.`;
    }
    case 'action': {
      const title = strField(cfg, 'customActionTitle') ?? strField(cfg, 'title') ?? 'Action';
      const desc = strField(cfg, 'actionDescription') ?? strField(cfg, 'description');
      const parts: string[] = [`**Action — ${title}**`];
      if (desc) parts.push(`> ${desc}`);
      const fields = arrField(cfg, 'formFields');
      for (const f of fields) {
        const name = strField(f, 'name');
        const value = strField(f, 'value');
        const type = strField(f, 'type');
        if (name && value !== undefined) {
          parts.push(`- field ${name}${type && type !== 'text' ? ` (${type})` : ''}: ${value}`);
        }
      }
      const capture = arrField(cfg, 'declaredOutputs');
      for (const c of capture) {
        if (typeof c === 'string') parts.push(`- capture: ${c}`);
      }
      const inline = arrField(cfg, 'inlineAssertions');
      for (const a of inline) {
        const cond = strField(a, 'condition');
        const sev = strField(a, 'severity') ?? 'fail';
        if (cond) parts.push(`- assert (${sev}): ${cond}`);
      }
      return parts.join('\n');
    }
    case 'assertion': {
      const cond = strField(cfg, 'condition') ?? strField(cfg, 'description') ?? '?';
      const sev = strField(cfg, 'severity') ?? 'fail';
      return `**Assert (${sev})** ${cond}`;
    }
    case 'wait': {
      const dur =
        (cfg as { waitDuration?: number; delaySeconds?: number }).waitDuration ??
        (cfg as { delaySeconds?: number }).delaySeconds ??
        0;
      return `**Wait** ${dur}s.`;
    }
    case 'login': {
      const role = strField(cfg, 'credentialTemplateName');
      const cred = strField(cfg, 'credentialName');
      if (role) return `**Switch role to** ${role}.`;
      if (cred) return `**Switch role to** credential ${cred}.`;
      return `**Log in.**`;
    }
    case 'seed': {
      const name = strField(cfg, 'seedWorkflowName') ?? '?';
      return `**Seed** \`${name}\`.`;
    }
    case 'setup': {
      const name = strField(cfg, 'setupFlowName') ?? '?';
      return `**Setup** \`${name}\`.`;
    }
    case 'condition': {
      const expr = strField(cfg, 'conditionExpression') ?? strField(cfg, 'description') ?? '?';
      return `**If** ${expr}`;
    }
    case 'notification': {
      const to = strField(cfg, 'to') ?? '?';
      const body = strField(cfg, 'body') ?? '';
      return `**Notify** ${to} — ${body}`;
    }
    case 'api_sequence': {
      const title = strField(cfg, 'customActionTitle') ?? strField(cfg, 'title') ?? 'API sequence';
      return `**API sequence — ${title}**`;
    }
    case 'sandbox_action': {
      const slug = strField(cfg, 'actionSlug') ?? '?';
      return `**Sandbox action — ${slug}**`;
    }
    case 'mcp': {
      const title = strField(cfg, 'title') ?? 'MCP';
      const instructions = strField(cfg, 'instructions');
      return instructions
        ? `**MCP — ${title}**\n> ${instructions.replace(/\n/g, '\n> ')}`
        : `**MCP — ${title}**`;
    }
    case 'end': {
      const outcome = strField(cfg, 'outcome') ?? 'pass';
      return outcome === 'pass' ? `**End**.` : `**End (${outcome}).**`;
    }
    default: {
      const title = strField(cfg, 'title') ?? strField(cfg, 'customActionTitle') ?? node.nodeType;
      return `**${node.nodeType} — ${title}**`;
    }
  }
}

function inferActor(workflow: LiveWorkflow): string | null {
  // Look for the leading login node and read the credential template name / static name.
  const sorted = topoOrderNodes(workflow.nodes, workflow.edges ?? []);
  const first = sorted[0];
  if (!first || first.nodeType !== 'login') return null;
  const cfg = first.configJson ?? {};
  const role = strField(cfg, 'credentialTemplateName');
  if (role) return role;
  const cred = strField(cfg, 'credentialName');
  if (cred) return cred;
  return null;
}

/** `- name: value`, or bare `- name:` when no default is declared. */
function renderInputLine(i: { name: string; defaultValue: string | undefined }): string {
  return i.defaultValue === undefined ? `- ${i.name}:` : `- ${i.name}: ${i.defaultValue}`;
}

function parseInputs(raw: unknown): Array<{ name: string; defaultValue: string | undefined }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ name: string; defaultValue: string | undefined }> = [];
  for (const i of raw) {
    if (!i || typeof i !== 'object') continue;
    const name = (i as { name?: unknown }).name;
    const def = (i as { defaultValue?: unknown }).defaultValue;
    // An input with no default (`defaultValue` omitted) must still show up
    // in the materialized view — dropping it silently made the declaration
    // unrepresentable in the surface agents read/edit (Flow Designer spec
    // view, release-qa spec-workspace.ts).
    if (typeof name === 'string') {
      out.push({ name, defaultValue: typeof def === 'string' ? def : undefined });
    }
  }
  return out;
}

function parseScenarios(raw: unknown): Array<{
  name: string;
  description?: string;
  isDefault?: boolean;
  values?: Record<string, string>;
}> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{
    name: string;
    description?: string;
    isDefault?: boolean;
    values?: Record<string, string>;
  }> = [];
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;
    const name = (s as { name?: unknown }).name;
    if (typeof name !== 'string') continue;
    const description = (s as { description?: unknown }).description;
    const isDefault = (s as { isDefault?: unknown }).isDefault;
    const values = (s as { values?: unknown }).values;
    const cleanValues: Record<string, string> = {};
    if (values && typeof values === 'object') {
      for (const [k, v] of Object.entries(values as Record<string, unknown>)) {
        if (typeof v === 'string') cleanValues[k] = v;
      }
    }
    out.push({
      name,
      ...(typeof description === 'string' ? { description } : {}),
      ...(typeof isDefault === 'boolean' ? { isDefault } : {}),
      ...(Object.keys(cleanValues).length > 0 ? { values: cleanValues } : {}),
    });
  }
  return out;
}

function yamlString(s: string): string {
  if (/^[A-Za-z0-9 _\-.,/]+$/.test(s)) return s;
  return JSON.stringify(s);
}
